import { useState, useRef, useEffect, KeyboardEvent, ChangeEvent } from "react";
import { DashboardLayout } from "@layouts/index";
import { createPin } from "@services/krent.service";
import { useSessionStorage } from "../../hooks/useSessionStorage";
import { toast } from "react-toastify";

interface PinModalProps {
  show: boolean;
  onClose: () => void;
  onSave: (pin: string) => void | Promise<void>;
  isSaving?: boolean;
  saveSucceeded?: boolean;
}

const BellIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#3bab8c"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

const KeyIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#1a6b50"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="7.5" cy="15.5" r="5.5" />
    <path d="M21 2l-9.6 9.6" />
    <path d="M15.5 7.5l3 3L22 7l-3-3" />
  </svg>
);

function getErrorMessage(error: any): string {
  return (
    error?.errors?.[0]?.message ||
    error?.message ||
    "Failed to create PIN. Please try again."
  );
}

function isPinAlreadyExistsError(error: any): boolean {
  const message = getErrorMessage(error).toLowerCase();
  return (
    message.includes("already") ||
    message.includes("exist") ||
    message.includes("has a pin") ||
    message.includes("has pin")
  );
}

// ── PIN Modal ──────────────────────────────────────────────────────────────
function PinModal({
  show,
  onClose,
  onSave,
  isSaving,
  saveSucceeded,
}: PinModalProps) {
  const [digits, setDigits] = useState<string[]>(["", "", "", ""]);
  const refs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];

  useEffect(() => {
    if (show) {
      setDigits(["", "", "", ""]);
      setTimeout(() => refs[0].current?.focus(), 120);
    }
  }, [show]);

  const handleChange = (i: number) => (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[i] = val;
    setDigits(next);
    if (val && i < 3) refs[i + 1].current?.focus();
  };

  const handleKeyDown = (i: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i] && i > 0) {
      refs[i - 1].current?.focus();
    }
  };

  const handleSave = () => {
    const pin = digits.join("");
    if (pin.length === 4 && !isSaving) {
      void onSave(pin);
    }
  };

  const complete = digits.every((d) => d !== "");

  if (!show) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={isSaving ? undefined : onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.55)",
          backdropFilter: "blur(4px)",
          zIndex: 1040,
          animation: "fadeIn 0.2s ease",
        }}
      />

      {/* Modal Card */}
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          zIndex: 1050,
          width: "min(420px, 92vw)",
          background: "#fff",
          borderRadius: "20px",
          padding: "40px 32px 36px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
          animation: "slideUp 0.25s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          disabled={isSaving}
          style={{
            position: "absolute",
            top: 18,
            right: 20,
            background: "none",
            border: "none",
            fontSize: 20,
            color: "#999",
            cursor: isSaving ? "not-allowed" : "pointer",
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {/* Icon */}
        <div className="d-flex justify-content-center mb-4">
          <div
            style={{
              width: 80,
              height: 80,
              background: "#edf7f4",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <KeyIcon />
          </div>
        </div>

        {/* Title */}
        <h5
          className="text-center fw-bold mb-2"
          style={{
            color: "#1a1a1a",
            fontSize: "1.35rem",
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Set Caution Fee PIN
        </h5>
        <p
          className="text-center mb-4"
          style={{ color: "#6b7280", fontSize: "0.92rem", lineHeight: 1.6 }}
        >
          Enter a 4-digit numeric PIN that you will use to authorize all
          transactions.
        </p>

        {/* PIN inputs */}
        <div className="d-flex justify-content-center gap-3 mb-4">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={refs[i]}
              type="password"
              inputMode="numeric"
              maxLength={1}
              value={d}
              disabled={isSaving || saveSucceeded}
              onChange={handleChange(i)}
              onKeyDown={handleKeyDown(i)}
              style={{
                width: 64,
                height: 72,
                textAlign: "center",
                fontSize: d ? "2rem" : "1.4rem",
                fontWeight: 700,
                border: `2px solid ${d ? "#3bab8c" : "#e5e7eb"}`,
                borderRadius: 14,
                outline: "none",
                background: d ? "#f0fbf7" : "#fafafa",
                color: "#1a1a1a",
                transition:
                  "border-color 0.2s, background 0.2s, transform 0.15s",
                transform: d ? "scale(1.06)" : "scale(1)",
                boxShadow: d ? "0 0 0 3px rgba(59,171,140,0.15)" : "none",
              }}
              placeholder="*"
            />
          ))}
        </div>

        <button
          onClick={handleSave}
          disabled={!complete || isSaving || saveSucceeded}
          style={{
            width: "100%",
            padding: "16px",
            background: complete
              ? saveSucceeded
                ? "#2e9e7a"
                : "linear-gradient(135deg,#3bab8c,#2e9e7a)"
              : "#d1d5db",
            color: "#fff",
            border: "none",
            borderRadius: 14,
            fontSize: "1rem",
            fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
            cursor:
              complete && !isSaving && !saveSucceeded ? "pointer" : "not-allowed",
            transition: "all 0.25s",
            letterSpacing: "0.03em",
            boxShadow: complete ? "0 4px 16px rgba(59,171,140,0.35)" : "none",
          }}
        >
          {isSaving ? (
            <span className="spinner-border spinner-border-sm"></span>
          ) : saveSucceeded ? (
            "✓ PIN Saved!"
          ) : (
            "Save PIN"
          )}
        </button>
      </div>
    </>
  );
}

export default function CautionFeeSetup({
  onSave,
}: {
  onSave?: (pin: string) => void;
}) {
  const [showModal, setShowModal] = useState(false);
  const [pinSet, setPinSet] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSucceeded, setSaveSucceeded] = useState(false);
  const [user, setUser] = useSessionStorage("user", {});

  const markPinConfigured = (pin: string) => {
    const updatedUser = { ...user, hasPIN: true };
    setUser(updatedUser);
    setPinSet(true);
    setSaveSucceeded(true);
    setShowModal(false);
    if (onSave) {
      onSave(pin);
    }
  };

  const handleSave = async (pin: string) => {
    if (!user?.wallet) {
      toast.error("Wallet not found. Please refresh and try again.");
      return;
    }

    setIsSaving(true);
    setSaveSucceeded(false);
    try {
      const abortController = new AbortController();
      await createPin(abortController.signal, {
        walletId: user.wallet,
        pin,
        pinConfirm: pin,
      });
      toast.success("PIN created successfully!");
      markPinConfigured(pin);
    } catch (error: any) {
      // PIN may already exist on the server while local session still has hasPIN: false
      if (isPinAlreadyExistsError(error)) {
        toast.success("Your PIN is already set up.");
        markPinConfigured(pin);
        return;
      }
      toast.error(getErrorMessage(error));
      console.error("PIN creation error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <DashboardLayout
      title="Caution Fee Setup"
      subtitle="Set up your transaction PIN"
    >
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <style>{`
        @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes slideUp {
          from { opacity:0; transform:translate(-50%,-44%) }
          to   { opacity:1; transform:translate(-50%,-50%) }
        }
        body { font-family:'DM Sans',sans-serif; }
        .setup-card:hover { box-shadow: 0 8px 32px rgba(59,171,140,0.18) !important; }
        .get-started-btn:hover {
          background: linear-gradient(135deg,#2e9e7a,#26866a) !important;
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(59,171,140,0.4) !important;
        }
        .get-started-btn:active { transform: translateY(0); }
      `}</style>

      {/* Full-page centered layout */}
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ minHeight: "75vh" }}
      >
        {/* Setup Card */}
        <div
          className="setup-card text-center"
          style={{
            width: "min(520px, 92vw)",
            padding: "52px 48px 48px",
            background: "#fff",
            border: "1.5px solid #b2dfd3",
            borderRadius: 20,
            boxShadow: "0 2px 16px rgba(59,171,140,0.08)",
            transition: "box-shadow 0.3s",
          }}
        >
          <div className="d-flex justify-content-center mb-4">
            <div
              style={{
                width: 76,
                height: 76,
                background: "#edf7f4",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <BellIcon />
            </div>
          </div>

          {/* Heading */}
          <h4
            style={{
              fontFamily: "'DM Sans',sans-serif",
              fontWeight: 700,
              color: "#111827",
              fontSize: "1.4rem",
              marginBottom: 16,
            }}
          >
            {pinSet ? "Bank details successful" : "First-Time Setup?"}
          </h4>

          {/* Description */}
          <p
            style={{
              color: "#4b5563",
              fontSize: "0.95rem",
              lineHeight: 1.7,
              marginBottom: 32,
              margin: "0 auto 32px",
            }}
          >
            {pinSet
              ? "Your Caution Fee PIN has been set successfully. You can now make secure withdrawals."
              : "Set a Caution Fee PIN for secure withdrawals and add Bank Account Details"}
          </p>

          {!pinSet && (
            <button
              className="get-started-btn bg-primary text-white"
              onClick={() => {
                setSaveSucceeded(false);
                setShowModal(true);
              }}
              style={{
                padding: "12px 36px",
                border: "none",
                borderRadius: 10,
                fontSize: "0.92rem",
                fontWeight: 700,
                fontFamily: "'DM Sans',sans-serif",
                cursor: "pointer",
                letterSpacing: "0.04em",
                transition: "all 0.22s",
              }}
            >
              Get started
            </button>
          )}

          {pinSet && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                color: "#3bab8c",
                fontWeight: 600,
                fontSize: "0.95rem",
              }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#3bab8c"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
              PIN configured
            </span>
          )}
        </div>
      </div>

      {/* PIN Modal */}
      <PinModal
        show={showModal}
        onClose={() => !isSaving && setShowModal(false)}
        onSave={handleSave}
        isSaving={isSaving}
        saveSucceeded={saveSucceeded}
      />
    </DashboardLayout>
  );
}
